import { useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { usePageAccess } from '@/hooks/usePageAccess'
import { getFixedAssetSyncCheck, type SyncIssue } from '@/services/fixedAssets'
import { useFixedAssetsSyncStore } from '@/store/fixedAssetsSyncStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { formatFixedAssetMoney, parseMoney } from './fixedAssetsFormat'
import {
  formatUnmappedAccountIssue,
  isActionableSyncIssue,
  prefillFromUnmappedIssue,
  renameNoticeText,
} from './fixedAssetsSyncIssues'

function formatCheckAge(ranAt: string | null | undefined): string {
  if (!ranAt) return 'Never checked'

  const then = new Date(ranAt).getTime()
  if (Number.isNaN(then)) return 'Never checked'

  const diffMs = Date.now() - then
  if (diffMs < 60_000) return 'Checked just now'

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) {
    return `Checked ${minutes} minute${minutes === 1 ? '' : 's'} ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `Checked ${hours} hour${hours === 1 ? '' : 's'} ago`
  }

  const days = Math.floor(hours / 24)
  return `Checked ${days} day${days === 1 ? '' : 's'} ago`
}

function issueRowClass(severity: SyncIssue['severity']): string {
  switch (severity) {
    case 'error':
      return 'border-red-200 bg-red-50 text-red-900'
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-900'
    default:
      return 'border-gray-200 bg-gray-50 text-gray-700'
  }
}

function isZeroBalanceUnmapped(issue: SyncIssue): boolean {
  return issue.type === 'unmapped_account' && Math.abs(parseMoney(issue.delta)) < 0.005
}

export default function FixedAssetsSyncBanner() {
  const { canEdit } = usePageAccess('fixed-assets')
  const latest = useFixedAssetsSyncStore((s) => s.latest)
  const setLatest = useFixedAssetsSyncStore((s) => s.setLatest)
  const ignoreAccounts = useFixedAssetsSyncStore((s) => s.ignoreAccounts)
  const openCreateModal = useFixedAssetsSyncStore((s) => s.openCreateModal)
  const [rechecking, setRechecking] = useState(false)
  const [ignoringId, setIgnoringId] = useState<string | null>(null)
  const [ignoringZeros, setIgnoringZeros] = useState(false)

  async function handleRecheck() {
    if (rechecking) return
    setRechecking(true)
    try {
      const check = await getFixedAssetSyncCheck({ refresh: true })
      setLatest(check)
      const actionable = check.issues.filter(isActionableSyncIssue)
      const renameNote = renameNoticeText(check.issues)
      if (check.ok && actionable.length === 0) {
        toast.success(renameNote ?? 'QuickBooks is in sync.')
      } else if (check.error_count > 0) {
        toast.error(
          `Sync check found ${check.error_count} error${check.error_count === 1 ? '' : 's'}.`,
        )
      } else {
        toast.success('Sync check complete.')
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Sync check failed'))
    } finally {
      setRechecking(false)
    }
  }

  async function handleIgnore(issue: SyncIssue) {
    if (!issue.qbo_account_id) return
    setIgnoringId(issue.qbo_account_id)
    try {
      await ignoreAccounts([issue.qbo_account_id])
      toast.success(`Ignored ${issue.name}.`)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to ignore account'))
    } finally {
      setIgnoringId(null)
    }
  }

  function handleCreate(issue: SyncIssue) {
    openCreateModal(prefillFromUnmappedIssue(issue, latest?.account_snapshot))
  }

  async function handleIgnoreZeroUnmapped() {
    const ids = zeroUnmapped.map((issue) => issue.qbo_account_id).filter(Boolean)
    if (ids.length === 0) return
    const confirmed = window.confirm(
      `Ignore ${ids.length} unmapped account${ids.length === 1 ? '' : 's'} with a $0.00 balance? They will stay off this list unless you remove them from the ignore list later.`,
    )
    if (!confirmed) return

    setIgnoringZeros(true)
    try {
      await ignoreAccounts(ids)
      toast.success(
        `Ignored ${ids.length} zero-balance account${ids.length === 1 ? '' : 's'}.`,
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to ignore zero-balance accounts'))
    } finally {
      setIgnoringZeros(false)
    }
  }

  const issues = latest?.issues ?? []
  const actionableIssues = issues.filter(isActionableSyncIssue)
  const hasIssues = actionableIssues.length > 0
  const inSync = latest != null && latest.ok && !hasIssues
  const renameNote = renameNoticeText(issues)
  const zeroUnmapped = issues.filter(isZeroBalanceUnmapped)
  const busy = rechecking || ignoringId != null || ignoringZeros

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {inSync ? (
            <p className="text-sm font-medium text-emerald-700">QBO in sync</p>
          ) : (
            <p className="text-sm font-medium text-gray-900">
              {hasIssues ? 'QuickBooks sync issues' : 'QuickBooks sync status'}
            </p>
          )}
          <p className="mt-0.5 text-xs text-gray-500">
            {formatCheckAge(latest?.ran_at)}
            {hasIssues
              ? ` · ${latest?.error_count ?? 0} error${(latest?.error_count ?? 0) === 1 ? '' : 's'}, ${latest?.warning_count ?? 0} warning${(latest?.warning_count ?? 0) === 1 ? '' : 's'}`
              : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canEdit && zeroUnmapped.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void handleIgnoreZeroUnmapped()}
            >
              {ignoringZeros
                ? 'Ignoring…'
                : `Ignore all $0 unmapped (${zeroUnmapped.length})`}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void handleRecheck()}
          >
            {rechecking ? 'Checking…' : 'Re-check QBO'}
          </Button>
        </div>
      </div>

      {renameNote ? (
        <p className="mt-2 text-xs text-gray-500">{renameNote}</p>
      ) : null}

      {hasIssues ? (
        <ul className="mt-3 max-h-80 space-y-2 overflow-y-auto">
          {actionableIssues.map((issue, index) => {
            const isUnmapped =
              canEdit && issue.type === 'unmapped_account' && Boolean(issue.qbo_account_id)
            return (
              <li
                key={`${issue.type}-${issue.qbo_account_id}-${index}`}
                className={`flex flex-nowrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${issueRowClass(issue.severity)}`}
              >
                <div className="min-w-0 flex-1">
                  {issue.type === 'unmapped_account' ? (
                    <div className="tabular-nums">{formatUnmappedAccountIssue(issue)}</div>
                  ) : (
                    <>
                      <div className="font-medium">{issue.name}</div>
                      <div className="mt-0.5">{issue.message}</div>
                      {issue.delta ? (
                        <div className="mt-0.5 tabular-nums text-xs opacity-90">
                          Delta: {formatFixedAssetMoney(issue.delta)}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {isUnmapped ? (
                  <div className="flex shrink-0 flex-nowrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => handleCreate(issue)}
                    >
                      Create asset
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void handleIgnore(issue)}
                    >
                      {ignoringId === issue.qbo_account_id ? 'Ignoring…' : 'Ignore'}
                    </Button>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
